import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import merge from "lodash/merge";
import spacing from "@ui/utils/spacing";
import media from "@ui/utils/media";
import widthLimited from "@ui/components/Decorators/widthLimited";
import ProductListHeaderSkeleton from "./ProductListHeaderSkeleton";
import ReadMore from "../../ReadMore";
import renderNodeOrComponent from "../../../utils/RenderNodeOrComponent";
import Text from "@ui/components/Text";

function ProductsListHeader({
  styles,
  skeleton,
  title,
  description,
  ViewAllComponent,
  TitleComponent,
  FiltersComponent,
  alignFiltersWithDescription = false,
}) {
  const _styles = merge({}, defaultStyles, styles);
  return (
    <Container styles={_styles.root}>
      {skeleton && !title && <ProductListHeaderSkeleton styles={_styles} />}
      {title && (
        <TitleSection viewAllPosition={_styles.viewAll.position}>
          <TitleComponent variant={"h1"} title={title} styles={_styles.title}>
            {title}
          </TitleComponent>
          {ViewAllComponent && renderNodeOrComponent(ViewAllComponent)}
          {FiltersComponent &&
            !alignFiltersWithDescription &&
            renderNodeOrComponent(FiltersComponent)}
        </TitleSection>
      )}
      {title && (
        <DescriptionContainer>
          <DescriptionSection styles={_styles.descriptionContainer}>
            <ReadMore text={description} styles={_styles.description} />
          </DescriptionSection>
          {FiltersComponent &&
            alignFiltersWithDescription &&
            renderNodeOrComponent(FiltersComponent)}
        </DescriptionContainer>
      )}
    </Container>
  );
}

const defaultStyles = {
  root: {},
  title: {
    fontFamily: "sans-serif",
    backgroundColor: "none",
    color: "black",
    fontWeight: "900",
    textTransform: "none",
    textAlign: "left",
    lineHeight: "1.5",
    fontSize: {
      lg: "32px",
      md: "32px",
      sm: "16px",
    },
  },
  descriptionContainer: {
    maxWidth: {
      lg: "75%",
      md: "75%",
      sm: "100vw",
    },
  },
  description: {},
  viewAll: {},
  filters: {},
};

const Container = widthLimited(styled.div.attrs(() => ({
  className: "products-list-header products-list-header__container",
}))`
  width: 100%;
  display: flex;
  flex-direction: column;
`);

const TitleSection = styled.div.attrs(() => ({
  className: "products-list-header products-list-header__title-section",
}))`
  display: flex;
  justify-content: ${({viewAllPosition}) =>
    viewAllPosition === "left" ? "flex-start" : "space-between"};
  column-gap: 8px;
  align-items: center;
  margin-bottom: ${spacing(2)};
`;

const DescriptionContainer = styled.div.attrs(() => ({
  className: "products-list-header products-list-header__description-container",
}))`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DescriptionSection = styled.div.attrs(() => ({
  className: "products-list-header products-list-header__description-section",
}))`
  display: flex;
  max-width: ${({styles}) => styles.maxWidth.lg};
  ${media.down("md")} {
    max-width: ${({styles}) => styles.maxWidth.md};
  }
  ${media.down("sm")} {
    max-width: ${({styles}) => styles.maxWidth.sm};
  }
`;

ProductsListHeader.propTypes = {
  styles: PropTypes.shape({
    root: PropTypes.object,
    title: PropTypes.shape({
      fontFamily: PropTypes.string,
      backgroundColor: PropTypes.string,
      color: PropTypes.string,
      lineHeight: PropTypes.string,
      fontSize: PropTypes.shape({
        lg: PropTypes.string,
        md: PropTypes.string,
        sm: PropTypes.string,
      }),
    }),
    descriptionContainer: PropTypes.shape({
      maxWidth: PropTypes.shape({
        lg: PropTypes.string,
        md: PropTypes.string,
        sm: PropTypes.string,
      }),
    }),
    description: PropTypes.object,
    viewAll: PropTypes.object,
    filters: PropTypes.object,
  }),
  title: PropTypes.string,
  description: PropTypes.string,
  ViewAllComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  FiltersComponent: PropTypes.elementType,
  TitleComponent: PropTypes.elementType,
  skeleton: PropTypes.bool,
  alignFiltersWithDescription: PropTypes.bool,
};

ProductsListHeader.defaultProps = {
  styles: defaultStyles,
  // eslint-disable-next-line react/prop-types
  TitleComponent: ({title, styles}) => (
    <Text variant={"h1"} text={title} styles={styles} />
  ),
};

export default ProductsListHeader;
